import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Check, Package, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const H = { fontFamily: 'Bebas Neue, sans-serif' };

export default function GearBoxSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id') || '';
  const [state, setState] = useState('checking'); // checking | paid | timeout | error
  const [tier, setTier] = useState('');
  const tries = useRef(0);

  useEffect(() => {
    document.title = 'Gear Box — Order Status | NinjAthlete';
    if (!sessionId) { setState('error'); return; }
    let stopped = false;
    const poll = async () => {
      if (stopped) return;
      tries.current += 1;
      try {
        const r = await fetch(`${API}/api/gearbox/status/${encodeURIComponent(sessionId)}`);
        if (!r.ok) throw new Error('status failed');
        const d = await r.json();
        if (d.payment_status === 'paid') { setTier(d.tier); setState('paid'); return; }
      } catch (e) { /* keep polling */ }
      if (tries.current >= 20) { setState('timeout'); return; }
      setTimeout(poll, 2500);
    };
    poll();
    return () => { stopped = true; };
  }, [sessionId]);

  return (
    <div className="bg-[#0a0a0a] min-h-[60vh]" data-testid="gearbox-success-page">
      <div className="max-w-2xl mx-auto px-5 pt-28 pb-24 text-center">
        {state === 'checking' && (
          <div data-testid="gearbox-success-checking">
            <RefreshCw className="w-8 h-8 text-[#C7FA3E] mx-auto mb-4 animate-spin" />
            <h1 className="text-white text-3xl mb-2" style={H}>Confirming your subscription…</h1>
            <p className="text-zinc-500 text-sm">Hold tight — verifying your payment with Stripe.</p>
          </div>
        )}
        {state === 'paid' && (
          <div className="border border-[#C7FA3E]/40 bg-[#C7FA3E]/[0.04] p-10" data-testid="gearbox-success-paid">
            <Check className="w-10 h-10 text-[#C7FA3E] mx-auto mb-4" />
            <h1 className="text-white text-4xl mb-3" style={H}>You're In!</h1>
            <p className="text-zinc-300 text-sm mb-2">
              Your <strong className="text-white capitalize">{tier}</strong> Gear Box subscription is active.
              A confirmation email is on its way.
            </p>
            <p className="text-zinc-500 text-xs mb-6 flex items-center justify-center gap-1.5"><Package className="w-3.5 h-3.5" /> First box ships the first week of next month.</p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/shop" className="bg-[#C7FA3E] text-black text-xs font-bold uppercase tracking-wider px-5 py-3 hover:bg-[#d4ff5c] transition-colors" data-testid="gearbox-success-shop-link">Browse the store</Link>
              <Link to="/gear-box" className="border border-zinc-700 text-zinc-300 text-xs font-bold uppercase tracking-wider px-5 py-3 hover:border-[#C7FA3E] transition-colors" data-testid="gearbox-success-back-link">Back to Gear Box</Link>
            </div>
          </div>
        )}
        {(state === 'timeout' || state === 'error') && (
          <div className="border border-amber-500/30 bg-amber-500/[0.04] p-10" data-testid="gearbox-success-pending">
            <h1 className="text-white text-3xl mb-3" style={H}>{state === 'error' ? 'Missing order reference' : 'Still processing'}</h1>
            <p className="text-zinc-400 text-sm mb-6">
              {state === 'error'
                ? 'We could not find an order reference in this link. If you completed checkout, watch your inbox for the confirmation email.'
                : 'Your payment is still being confirmed. If you completed checkout you will receive a confirmation email shortly — no need to purchase again.'}
            </p>
            <Link to="/gear-box" className="text-[#C7FA3E] text-xs uppercase tracking-wider hover:underline">← Back to Gear Box</Link>
          </div>
        )}
      </div>
    </div>
  );
}
